import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useState } from 'react';
import { Page, Layout, Card, TextField, ButtonGroup, Button, Checkbox, Text, Banner, Popover, ActionList, InlineGrid, BlockStack, InlineStack, Tooltip, Icon, } from '@shopify/polaris';
import { QuestionCircleIcon } from '@shopify/polaris-icons';
import { authFetch } from '../lib/api';
const DATE_RANGES = [
    { key: 'last7days', label: 'Last 7 days' },
    { key: 'last30days', label: 'Last 30 days' },
    { key: 'last90days', label: 'Last 90 days' },
    { key: 'thisMonth', label: 'This month' },
    { key: 'lastMonth', label: 'Last month' },
];
const FORMAT_LABELS = {
    xlsx: 'xlsx',
    json: 'JSON',
};
export default function HomePage() {
    const [fileName, setFileName] = useState('Customers SMS Export');
    const [dateRange, setDateRange] = useState('last7days');
    const [includeOrderTags, setIncludeOrderTags] = useState(false);
    const [includeEmails, setIncludeEmails] = useState(false);
    const [includeNames, setIncludeNames] = useState(true);
    const [format, setFormat] = useState('xlsx');
    const [counts, setCounts] = useState({
        customers: 0,
        phoneNumbers: 0,
        emailAddresses: 0,
    });
    const [runSnapshot, setRunSnapshot] = useState(null);
    const [running, setRunning] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [formatMenuActive, setFormatMenuActive] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const triggerDownload = useCallback(async (fmt, name, snapshot) => {
        const params = new URLSearchParams({
            dateRange: snapshot.dateRange,
            format: fmt,
            fileName: name,
            includeOrderTags: String(snapshot.includeOrderTags),
            includeEmails: String(snapshot.includeEmails),
            includeNames: String(snapshot.includeNames),
        });
        const res = await authFetch(`/api/customers/export?${params.toString()}`);
        if (!res.ok) {
            const text = await res.text();
            throw new Error(`Export failed (${res.status}): ${text.slice(0, 200)}`);
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `${name}.${fmt}`;
        anchor.rel = 'noopener';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    }, []);
    const handleRun = useCallback(async () => {
        setRunning(true);
        setError(null);
        setSuccess(null);
        try {
            const previewRes = await authFetch('/api/customers/preview', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ dateRange }),
            });
            if (!previewRes.ok) {
                const text = await previewRes.text();
                throw new Error(`Server responded with ${previewRes.status}: ${text.slice(0, 200)}`);
            }
            const previewJson = (await previewRes.json());
            if (previewJson.status !== 'ok' || !previewJson.data) {
                throw new Error(previewJson.message || 'Preview failed');
            }
            setCounts(previewJson.data);
            setRunSnapshot({
                dateRange,
                includeOrderTags,
                includeEmails,
                includeNames,
            });
            if (previewJson.data.phoneNumbers === 0) {
                setSuccess('No phone numbers found for the selected range.');
                setTimeout(() => setSuccess(null), 4000);
            }
        }
        catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        }
        finally {
            setRunning(false);
        }
    }, [dateRange, includeEmails, includeNames, includeOrderTags]);
    const handleExport = useCallback(async (fmt) => {
        if (!runSnapshot) {
            setError('Run the report before exporting.');
            return;
        }
        setExporting(true);
        setError(null);
        setSuccess(null);
        try {
            await triggerDownload(fmt, fileName.trim() || 'Customers SMS Export', runSnapshot);
            setSuccess(`Export ready — downloaded ${FORMAT_LABELS[fmt]} file.`);
            setTimeout(() => setSuccess(null), 4000);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        }
        finally {
            setExporting(false);
        }
    }, [fileName, runSnapshot, triggerDownload]);
    const exportDisabled = !runSnapshot || exporting;
    const formatMenuItems = Object.keys(FORMAT_LABELS).map((f) => ({
        content: `Export as ${FORMAT_LABELS[f]}`,
        disabled: exportDisabled,
        onAction: () => {
            setFormat(f);
            setFormatMenuActive(false);
            void handleExport(f);
        },
    }));
    return (_jsx(Page, { title: "Dashboard", primaryAction: {
            content: 'Run',
            onAction: () => void handleRun(),
            loading: running,
        }, children: _jsx(Layout, { children: _jsx(Layout.Section, { children: _jsxs(BlockStack, { gap: "400", children: [error && (_jsx(Banner, { tone: "critical", onDismiss: () => setError(null), children: _jsx("p", { children: error }) })), success && (_jsx(Banner, { tone: "success", onDismiss: () => setSuccess(null), children: _jsx("p", { children: success }) })), _jsx(Card, { children: _jsxs(BlockStack, { gap: "400", children: [_jsx(Text, { as: "h2", variant: "headingMd", children: "Export new customer's SMS numbers" }), _jsx(TextField, { label: "File name", value: fileName, onChange: setFileName, autoComplete: "off" }), _jsxs(BlockStack, { gap: "200", children: [_jsx(Text, { as: "span", variant: "bodyMd", children: "Date range" }), _jsx(ButtonGroup, { variant: "segmented", children: DATE_RANGES.map(({ key, label }) => (_jsx(Button, { pressed: dateRange === key, onClick: () => setDateRange(key), children: label }, key))) })] }), _jsxs(BlockStack, { gap: "200", children: [_jsx(Checkbox, { label: "Include order tags", checked: includeOrderTags, onChange: setIncludeOrderTags }), _jsx(Checkbox, { label: "Include customers' emails", checked: includeEmails, onChange: setIncludeEmails }), _jsx(Checkbox, { label: "Include customers' names", checked: includeNames, onChange: setIncludeNames })] })] }) }), _jsx(Text, { as: "h2", variant: "headingMd", children: "Export Data" }), _jsxs(InlineGrid, { columns: { xs: 1, sm: 3 }, gap: "400", children: [_jsx(MetricCard, { label: "Customers", value: counts.customers, help: "Customers created OR updated in the selected date range." }), _jsx(MetricCard, { label: "Phone numbers", value: counts.phoneNumbers, help: "Customers in the range that have a default phone number. Only these rows are written to the export \u2014 entries without a phone are skipped." }), _jsx(MetricCard, { label: "Email addresses", value: counts.emailAddresses, help: "Customers in the range that have a default email address." })] }), _jsx(InlineStack, { align: "end", children: _jsx(Popover, { active: formatMenuActive, activator: _jsx(Button, { onClick: () => setFormatMenuActive((v) => !v), disclosure: true, loading: exporting, disabled: !runSnapshot, children: `Export to ${FORMAT_LABELS[format]}` }), onClose: () => setFormatMenuActive(false), children: _jsx(ActionList, { items: formatMenuItems }) }) })] }) }) }) }));
}
function MetricCard({ label, value, help }) {
    return (_jsx(Card, { children: _jsxs(BlockStack, { gap: "200", children: [_jsxs(InlineStack, { align: "space-between", blockAlign: "center", wrap: false, children: [_jsx(Text, { as: "span", variant: "bodySm", tone: "subdued", children: label }), _jsx(Tooltip, { content: help, dismissOnMouseOut: true, children: _jsx("span", { "aria-label": help, style: {
                                    display: 'inline-flex',
                                    width: 'var(--p-icon-size-sm, 16px)',
                                    height: 'var(--p-icon-size-sm, 16px)',
                                }, children: _jsx(Icon, { source: QuestionCircleIcon, tone: "subdued" }) }) })] }), _jsx(Text, { as: "p", variant: "headingLg", fontWeight: "bold", children: String(value) })] }) }));
}
